/**
 * Missions.ts — Mission / objective system.
 *
 * Manages a sequence of aviation-themed mini-missions:
 *   1. Fly through glowing rings
 *   2. Land at a backcountry airstrip
 *   3. Deliver cargo between locations
 *   4. High-speed valley run
 *   5. Avoid storm clouds for a duration
 *   6. Discover hidden landmarks
 *
 * Each mission has its own setup, per-frame check, and teardown.
 */
import * as THREE from 'three';
export const MISSION_CATALOG = [
    'Ring Run',
    'Landing Challenge',
    'Cargo Run',
    'Valley Speed Run',
    'Storm Dodge',
    'Discover Landmarks',
];
/* ========================================================================= */
/*  1. Ring Run                                                              */
/* ========================================================================= */
class RingRunMission {
    constructor() {
        this.name = 'Ring Run';
        this.description = 'Fly through all the glowing rings!';
        this.rings = [];
        this.collected = 0;
        this.total = 8;
    }
    setup(game) {
        this.rings = [];
        this.collected = 0;
        const ringGeo = new THREE.TorusGeometry(10, 1.0, 8, 24);
        const ringMat = new THREE.MeshStandardMaterial({ color: 0x44ffaa, emissive: 0x22ff88, emissiveIntensity: 0.9, transparent: true, opacity: 0.8, flatShading: true });
        // Place rings in an arc / path
        const cx = game.player.mesh.position.x;
        const cz = game.player.mesh.position.z;
        for (let i = 0; i < this.total; i++) {
            const angle = (i / this.total) * Math.PI * 1.2 - Math.PI * 0.3;
            const r = 200 + i * 40;
            const x = cx + Math.cos(angle) * r;
            const z = cz + Math.sin(angle) * r;
            const terrainY = game.world.getTerrainHeight(x, z);
            const ringClearance = 50 + Math.sin(i * 0.7) * 30;
            const y = Math.max(terrainY + ringClearance, game.player.mesh.position.y + 15);
            const ring = new THREE.Mesh(ringGeo, ringMat.clone());
            ring.position.set(x, y, z);
            // Face roughly toward next ring
            ring.lookAt(cx, y, cz);
            game.scene.add(ring);
            this.rings.push(ring);
        }
    }
    update(game, dt) {
        for (const ring of this.rings) {
            if (!ring.visible)
                continue;
            ring.rotation.z += dt * 0.5;
            const d = ring.position.distanceTo(game.player.mesh.position);
            if (d < 16) {
                ring.visible = false;
                this.collected++;
                game.addScore(100, `Ring ${this.collected}/${this.total}!`);
            }
        }
        return this.collected >= this.total;
    }
    teardown(game) {
        for (const r of this.rings)
            game.scene.remove(r);
        this.rings = [];
    }
    getNavigationTarget(game) {
        const playerPos = game.player.mesh.position;
        let nearest = null;
        let nearestDist = Number.POSITIVE_INFINITY;
        for (const ring of this.rings) {
            if (!ring.visible)
                continue;
            const d = ring.position.distanceToSquared(playerPos);
            if (d < nearestDist) {
                nearestDist = d;
                nearest = ring;
            }
        }
        return nearest ? nearest.position.clone() : null;
    }
}
/* ========================================================================= */
/*  2. Landing Challenge                                                     */
/* ========================================================================= */
class LandingMission {
    constructor() {
        this.name = 'Landing Challenge';
        this.description = '';
        this.targetIdx = 0;
        this.landed = false;
        this.landTimer = 0;
    }
    setup(game) {
        // Pick a strip that isn't the one closest to the player
        const strips = game.world.airstrips;
        let best = 0, bestDist = 0;
        for (let i = 0; i < strips.length; i++) {
            const d = strips[i].position.distanceTo(game.player.mesh.position);
            if (d > bestDist) {
                bestDist = d;
                best = i;
            }
        }
        this.targetIdx = best;
        this.description = `Land at ${strips[this.targetIdx].name}!`;
        this.landed = false;
        this.landTimer = 0;
    }
    update(game, dt) {
        const strip = game.world.airstrips[this.targetIdx];
        const d = strip.position.distanceTo(game.player.mesh.position);
        if (d < 50 && game.player.onGround && game.player.speed < 5) {
            this.landTimer += dt;
            if (this.landTimer > 1.5 && !this.landed) {
                this.landed = true;
                game.addScore(300, `Landed at ${strip.name}!`);
            }
        }
        else {
            this.landTimer = 0;
        }
        return this.landed;
    }
    teardown(_game) { }
    getNavigationTarget(game) {
        const strip = game.world.airstrips[this.targetIdx];
        return strip ? strip.position.clone().add(new THREE.Vector3(0, 20, 0)) : null;
    }
}
/* ========================================================================= */
/*  3. Cargo Delivery                                                        */
/* ========================================================================= */
class CargoMission {
    constructor() {
        this.name = 'Cargo Run';
        this.description = '';
        this.fromIdx = 0;
        this.toIdx = 1;
        this.pickedUp = false;
        this.delivered = false;
    }
    setup(game) {
        const strips = game.world.airstrips;
        this.fromIdx = 0;
        this.toIdx = strips.length > 1 ? 1 : 0;
        // Pick furthest pair
        let maxD = 0;
        for (let a = 0; a < strips.length; a++) {
            for (let b = a + 1; b < strips.length; b++) {
                const d = strips[a].position.distanceTo(strips[b].position);
                if (d > maxD) {
                    maxD = d;
                    this.fromIdx = a;
                    this.toIdx = b;
                }
            }
        }
        this.description = `Pick up cargo at ${strips[this.fromIdx].name}, deliver to ${strips[this.toIdx].name}`;
        this.pickedUp = false;
        this.delivered = false;
        // Cargo crate marker at pickup strip
        const geo = new THREE.BoxGeometry(3, 3, 3);
        const mat = new THREE.MeshStandardMaterial({ color: 0xff8800, emissive: 0xff6600, emissiveIntensity: 0.5, flatShading: true });
        this.marker = new THREE.Mesh(geo, mat);
        const pos = strips[this.fromIdx].position;
        this.marker.position.set(pos.x + 8, pos.y + 2, pos.z);
        game.scene.add(this.marker);
    }
    update(game, dt) {
        const strips = game.world.airstrips;
        if (!this.pickedUp) {
            this.marker.rotation.y += dt;
            const d = strips[this.fromIdx].position.distanceTo(game.player.mesh.position);
            if (d < 40 && game.player.onGround && game.player.speed < 5) {
                this.pickedUp = true;
                this.marker.visible = false;
                game.addScore(100, 'Cargo loaded!');
                this.description = `Deliver cargo to ${strips[this.toIdx].name}`;
            }
        }
        else if (!this.delivered) {
            const d = strips[this.toIdx].position.distanceTo(game.player.mesh.position);
            if (d < 40 && game.player.onGround && game.player.speed < 5) {
                this.delivered = true;
                game.addScore(400, 'Cargo delivered!');
            }
        }
        return this.delivered;
    }
    teardown(game) {
        game.scene.remove(this.marker);
    }
    getNavigationTarget(game) {
        const targetIdx = this.pickedUp ? this.toIdx : this.fromIdx;
        const strip = game.world.airstrips[targetIdx];
        return strip ? strip.position.clone().add(new THREE.Vector3(0, 20, 0)) : null;
    }
}
/* ========================================================================= */
/*  4. Valley Speed Run                                                      */
/* ========================================================================= */
class ValleySpeedRun {
    constructor() {
        this.name = 'Valley Speed Run';
        this.description = 'Fly through the valley at high speed! Stay below 80 m altitude for 20 s.';
        this.timer = 0;
        this.goal = 20;
    }
    setup(_game) {
        this.timer = 0;
    }
    update(game, dt) {
        const alt = game.player.mesh.position.y - game.world.getTerrainHeight(game.player.mesh.position.x, game.player.mesh.position.z);
        if (alt < 80 && game.player.speed > 40) {
            this.timer += dt;
        }
        else {
            this.timer = Math.max(0, this.timer - dt * 0.5); // slow penalty
        }
        // HUD feedback
        this.description = `Valley run: ${Math.floor(this.timer)}/${this.goal} s — SPD>${Math.round(game.player.speed)} ALT<${Math.round(alt)}`;
        if (this.timer >= this.goal) {
            game.addScore(500, 'Valley run complete!');
            return true;
        }
        return false;
    }
    teardown(_game) { }
    getNavigationTarget(_game) {
        return new THREE.Vector3(400, 80, 0);
    }
}
/* ========================================================================= */
/*  5. Storm Dodge                                                           */
/* ========================================================================= */
class StormDodgeMission {
    constructor() {
        this.name = 'Storm Dodge';
        this.description = 'Fly for 30 s without entering any storm cloud!';
        this.timer = 0;
        this.goal = 30;
    }
    setup(_game) { this.timer = 0; }
    update(game, dt) {
        let inStorm = false;
        for (const sc of game.world.stormClouds) {
            if (sc.center.distanceTo(game.player.mesh.position) < sc.radius * 0.8) {
                inStorm = true;
                break;
            }
        }
        if (inStorm) {
            this.timer = Math.max(0, this.timer - dt * 2);
        }
        else {
            this.timer += dt;
        }
        this.description = `Storm dodge: ${Math.floor(this.timer)}/${this.goal} s`;
        if (this.timer >= this.goal) {
            game.addScore(400, 'Storm dodged!');
            return true;
        }
        return false;
    }
    teardown(_game) { }
    getNavigationTarget(_game) {
        return null;
    }
}
/* ========================================================================= */
/*  6. Landmark Discovery                                                    */
/* ========================================================================= */
class LandmarkMission {
    constructor() {
        this.name = 'Discover Landmarks';
        this.description = 'Fly close to all hidden landmarks!';
        this.found = [];
        this.total = 0;
        this.foundCount = 0;
    }
    setup(game) {
        this.total = game.world.landmarks.length;
        this.found = new Array(this.total).fill(false);
        this.foundCount = 0;
    }
    update(game, _dt) {
        for (let i = 0; i < this.total; i++) {
            if (this.found[i])
                continue;
            const d = game.world.landmarks[i].distanceTo(game.player.mesh.position);
            if (d < 50) {
                this.found[i] = true;
                this.foundCount++;
                game.addScore(200, `Landmark ${this.foundCount}/${this.total}!`);
            }
        }
        this.description = `Landmarks: ${this.foundCount}/${this.total}`;
        return this.foundCount >= this.total;
    }
    teardown(_game) { }
    getNavigationTarget(game) {
        const playerPos = game.player.mesh.position;
        let nearest = null;
        let nearestDist = Number.POSITIVE_INFINITY;
        for (let i = 0; i < this.total; i++) {
            if (this.found[i])
                continue;
            const lm = game.world.landmarks[i];
            const d = lm.distanceToSquared(playerPos);
            if (d < nearestDist) {
                nearestDist = d;
                nearest = lm;
            }
        }
        return nearest ? nearest.clone().add(new THREE.Vector3(0, 15, 0)) : null;
    }
}
/* ========================================================================= */
/*  MissionManager                                                           */
/* ========================================================================= */
export class MissionManager {
    constructor(game, startMissionIndex = 0) {
        this.missions = [];
        this.currentIdx = 0;
        this.completed = [];
        this.awaitingSelection = false;
        this.game = game;
        // Build the mission list (in order)
        this.missions = [
            new RingRunMission(),
            new LandingMission(),
            new CargoMission(),
            new ValleySpeedRun(),
            new StormDodgeMission(),
            new LandmarkMission(),
        ];
        this.completed = new Array(this.missions.length).fill(false);
        this.currentIdx = THREE.MathUtils.clamp(startMissionIndex, 0, this.missions.length - 1);
        this.current.setup(game);
    }
    get current() { return this.missions[this.currentIdx]; }
    update(dt) {
        if (this.awaitingSelection)
            return;
        const done = this.current.update(this.game, dt);
        if (done) {
            this.completed[this.currentIdx] = true;
            this.awaitingSelection = true;
            this.showCompleteOverlay();
        }
    }
    advance(selectedMissionIndex) {
        this.current.teardown(this.game);
        const selectable = this.getSelectableMissionIndices();
        let nextIdx = selectable[0] ?? 0;
        if (selectedMissionIndex !== undefined && selectable.includes(selectedMissionIndex)) {
            nextIdx = selectedMissionIndex;
        }
        this.currentIdx = nextIdx;
        this.awaitingSelection = false;
        this.current.setup(this.game);
    }
    showCompleteOverlay() {
        document.getElementById('mission-complete-title').textContent = `${this.current.name} Complete!`;
        const selectable = this.getSelectableMissionIndices();
        const selectEl = document.getElementById('next-mission-select');
        selectEl.innerHTML = '';
        for (const idx of selectable) {
            const option = document.createElement('option');
            option.value = String(idx);
            option.textContent = `${idx + 1}. ${this.missions[idx].name}`;
            selectEl.appendChild(option);
        }
        if (selectable.length > 0) {
            selectEl.value = String(selectable[0]);
        }
        const progressDone = this.completed.filter(Boolean).length;
        document.getElementById('mission-complete-text').textContent =
            `Score: ${this.game.score} — Completed: ${progressDone}/${this.missions.length}. Choose your next mission.`;
        document.getElementById('mission-complete').classList.remove('hidden');
    }
    getSelectableMissionIndices() {
        const uncompleted = [];
        for (let i = 0; i < this.missions.length; i++) {
            if (!this.completed[i])
                uncompleted.push(i);
        }
        if (uncompleted.length > 0)
            return uncompleted;
        // If all missions are completed, start a new cycle and allow any mission.
        this.completed.fill(false);
        return this.missions.map((_, idx) => idx);
    }
    /** Provides the current mission description for the HUD. */
    get missionText() {
        return `[${this.currentIdx + 1}/${this.missions.length}] ${this.current.name}: ${this.current.description}`;
    }
    /** Objective world position for navigation UI. */
    get navigationTarget() {
        return this.current.getNavigationTarget(this.game);
    }
}
